import { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import './assets/css/App.css';
import './assets/css/Slider.css';
import './assets/css/videocontrols.css';
import CriticalStylesDefer from './components/CriticalStylesDefer';
import ResourcePrefetch from './components/ResourcePrefetch';
import Nav from './components/Nav';
import HeroSection from './components/HeroSection';
import HomePage from './components/HomePage';
import SliderWrap from './components/SliderWrap';
import ScrollWrap from './components/ScrollWrap';
import SliderWrap2 from './components/SliderWrap2';
import AIAudioSummarizerPage from './components/AIAudioSummarizerPage';
import ContactUs from './components/ContactUs';
import TermsConditions from './components/TermsConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import AboutUs from './components/AboutUs';
import Pricing from './components/Pricing';
import CookieConsent from './components/CookieConsent';
import { trackPageView } from './utils/analytics';

// Defer GSAP loading for better initial performance
let gsap, ScrollTrigger, SplitText, Lenis;

const loadGSAP = async () => {
  // Skip GSAP loading during pre-rendering (react-snap)
  if (navigator.userAgent === 'ReactSnap') {
    return { gsap: null, ScrollTrigger: null, SplitText: null, Lenis: null };
  }
  
  if (!gsap) {
    const [gsapModule, gsapPlugins, lenisModule] = await Promise.all([
      import('gsap'),
      import('gsap/all'),
      import('@studio-freight/lenis')
    ]);
    
    gsap = gsapModule.default;
    ScrollTrigger = gsapPlugins.ScrollTrigger;
    SplitText = gsapPlugins.SplitText;
    Lenis = lenisModule.default;
    
    gsap.registerPlugin(ScrollTrigger, SplitText);
  }
  return { gsap, ScrollTrigger, SplitText, Lenis };
};

// Google Analytics page tracking component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  const pageRef = useRef(null);
  const lenisRef = useRef(null);
  
  // Mark user as visited for analytics (optional)
  useEffect(() => {
    // Simple analytics tracking - mark user as visited
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('audiosum_visited', 'true');
    }
  }, []);

  useEffect(() => {
    // Skip during pre-rendering
    if (navigator.userAgent === 'ReactSnap') {
      return;
    }
    
    // Load GSAP and Lenis asynchronously for better performance
    const initScrollAndAnimations = async () => {
      try {
        const { Lenis: LenisClass } = await loadGSAP();
        
        // Skip if GSAP didn't load
        if (!LenisClass) return;
        
        // Detect if user is on Mac
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || 
                      navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
        
        // Add lenis class to html for CSS targeting
        document.documentElement.classList.add('lenis');
        
        lenisRef.current = new LenisClass({
          lerp: isMac ? 0.02 : 0.1, // Much slower lerp for Mac trackpads
          smooth: true,
          direction: 'vertical',
          gestureDirection: 'vertical',
          smoothTouch: false, // Disable smooth touch entirely to prevent conflicts
          touchMultiplier: isMac ? 0.5 : 2, // Reduce touch sensitivity on Mac
          wheelMultiplier: isMac ? 0.8 : 1, // Reduce wheel sensitivity on Mac
          syncTouch: false, // Disable touch syncing on Mac
          touchInertiaMultiplier: isMac ? 10 : 35, // Adjust inertia for Mac
        });

        const raf = (time) => {
          if (lenisRef.current) {
            lenisRef.current.raf(time);
            requestAnimationFrame(raf);
          }
        };
        requestAnimationFrame(raf);
      } catch (error) {
        console.warn('Failed to load smooth scrolling:', error);
      }
    };

    // Defer initialization to avoid blocking initial render
    setTimeout(initScrollAndAnimations, 100);

    // Initialize other animations after GSAP is loaded
    const initOtherAnimations = async () => {
      try {
        await loadGSAP(); // Ensure GSAP is loaded
        initBlurAnimations();
        initVideoPlayback();
        initPlayPauseVideoScroll();
      } catch (error) {
        console.warn('Failed to load animations:', error);
      }
    };

    // Defer other animations too
    setTimeout(initOtherAnimations, 200);

    return () => {
      // Cleanup GSAP animations if loaded
      if (ScrollTrigger && ScrollTrigger.getAll) {
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      }
      if (gsap && gsap.globalTimeline) {
        gsap.globalTimeline.clear();
      }

      // Remove lenis class and cleanup
      document.documentElement.classList.remove('lenis');

      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
    };
  }, []);

  // const initAnnouncement = () => {
  //   const announcement = document.querySelector('.announcement_wrap');
  //   if (!announcement) return;

  //   const closeBtn = announcement.querySelector('.announcement_close');
  //   if (closeBtn) {
  //     closeBtn.addEventListener('click', () => {
  //       gsap.to(announcement, {
  //         opacity: 0,
  //         height: 0,
  //         padding: 0,
  //         duration: 0.3,
  //         onComplete: () => {
  //           announcement.style.display = 'none';
  //         }
  //       });
  //       sessionStorage.setItem('announcement', 'false');
  //     });
  //   }

  //   if (sessionStorage.getItem('announcement') === 'false') {
  //     announcement.style.display = 'none';
  //   }
  // };


  // const initPageTransition = () => {
  //   const transitionWrap = document.querySelector('.transition_wrap');
  //   if (!transitionWrap) return;

  //   // Handle internal link transitions
  //   document.querySelectorAll('a').forEach(link => {
  //     const href = link.getAttribute('href');

  //     // Skip if not internal link or has prevent attribute
  //     if (!href ||
  //       href.startsWith('#') ||
  //       link.target === '_blank' ||
  //       link.hasAttribute('data-transition-prevent')) {
  //       return;
  //     }

  //     try {
  //       const url = new URL(link.href, window.location.origin);
  //       if (url.hostname !== window.location.hostname) return;
  //     } catch (e) {
  //       return;
  //     }

  //     link.addEventListener('click', (e) => {
  //       e.preventDefault();
  //       const targetUrl = link.href;

  //       gsap.timeline({
  //         onStart: () => gsap.set(transitionWrap, { display: 'block' }),
  //         onComplete: () => gsap.set(transitionWrap, { display: 'none' })
  //       })
  //         .to('.transition_layout', { opacity: 1, duration: 0.3 })
  //         .to('.transition_layout', {
  //           opacity: 0,
  //           duration: 0.3,
  //           delay: 0.2,
  //           onComplete: () => {
  //             window.location.href = targetUrl;
  //           }
  //         });
  //     });
  //   });
  // };

  


  const initBlurAnimations = () => {
    if (!gsap || !ScrollTrigger) return; // Skip if GSAP not loaded
    
    document.querySelectorAll('[data-animate="blur"]').forEach(el => {
      gsap.fromTo(el,
        {
          opacity: 0,
          filter: "blur(2rem)",
          scale: 0.8,
        },
        {
          opacity: 1,
          filter: "blur(0rem)",
          scale: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top bottom",
            end: "top 75%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  };

  const initVideoPlayback = () => {
    if (!ScrollTrigger) return; // Skip if ScrollTrigger not loaded
    
    document.querySelectorAll('[data-video="playpause"]').forEach(video => {
      
      ScrollTrigger.create({
        trigger: video,
        start: '0% 100%',
        end: '100% 0%',
        onEnter: () => video.play(),
        onEnterBack: () => video.play()
        // onLeave: () => video.pause(),
        // onLeaveBack: () => video.pause()
      });
    });
  };

  const initPlayPauseVideoScroll = () => {
    if (!ScrollTrigger) return; // Skip if ScrollTrigger not loaded
    
    document.querySelectorAll('[data-video="scroll"]').forEach(videoWrap => {
      const video = videoWrap.querySelector('video');
      if (!video) return;

      ScrollTrigger.create({
        trigger: videoWrap,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => video.play(),
        onEnterBack: () => video.play()
        // onLeave: () => video.pause(),
        // onLeaveBack: () => video.pause()
      });
    });
  };

  return (
    <HelmetProvider>
      <LanguageProvider>
        <Router>
          <div ref={pageRef}>
            <CriticalStylesDefer />
            <ResourcePrefetch />
          <div className="page_wrap">
        <div className="page_code_wrap">
          <div className="page_code_base w-embed">
          </div>
          <div className="page_code_custom w-embed">
          </div>
          <div className="styleguide_guide_wrap">
            <div className="w-embed">
            </div>
            <div data-padding-bottom="none" data-padding-top="none" className="styleguide_guide_contain u-container">
              <div className="styleguide_guide_layout u-grid-custom">
                <div className="styleguide_guide_column u-vflex-center-bottom">
                </div>
                <div className="styleguide_guide_column u-vflex-center-bottom">
                </div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
                <div className="styleguide_guide_column u-vflex-center-bottom"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="transition_wrap" style={{ display: 'none' }}>
          <div className="transition_layout"></div>
        </div>
        
        <PageTracker />
        
        <Routes>
          {/* Default route redirects to English */}
          <Route path="/" element={<Navigate to="/en" replace />} />
          
          {/* Language routes */}
          <Route path="/:lang" element={<HomePage />} />
          
          {/* AI Audio Summarizer routes */}
          <Route path="/:lang/ai-audio-summarizer" element={<AIAudioSummarizerPage />} />
          
          {/* About Us routes */}
          <Route path="/:lang/about" element={<AboutUs />} />
          
          {/* Pricing routes */}
          <Route path="/:lang/pricing" element={<Pricing />} />
          
          {/* Contact Us routes */}
          <Route path="/:lang/contact" element={<ContactUs />} />
          
          {/* Terms & Conditions routes */}
          <Route path="/:lang/terms-of-service" element={<TermsConditions />} />
          
          {/* Privacy Policy routes */}
          <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
          
          {/* Fallback for old structure - show original content */}
          <Route path="/original" element={
            <div>
              <Nav />
              <HeroSection />
              <SliderWrap />
              <ScrollWrap />
              <SliderWrap2 />
            </div>
          } />
        </Routes>
        
        <CookieConsent />
      </div>
    </div>
        </Router>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
